import { ChangeDetectorRef, OnDestroy, Pipe, PipeTransform, inject } from '@angular/core';
import { AsyncPipe } from '@angular/common';
import { Observable, catchError, map, of, startWith } from 'rxjs';
import { ApiState, extractHttpErrorMessage } from '../misc/api-state';

/**
 * Drop-in replacement for `| async` that catches errors instead of throwing.
 * Subscribes to an `Observable<T>` and returns `ApiState<T>`.
 *
 * Delegates subscription lifecycle to Angular's `AsyncPipe` internally —
 * inheriting its `untracked()` wrapping, `markForCheck` optimizations,
 * and memory-leak prevention.
 */
@Pipe({
  name: 'apiAsync',
  standalone: true,
  pure: false,
})
export class ApiAsyncPipe<T> implements PipeTransform, OnDestroy {
  private readonly asyncPipe = new AsyncPipe(inject(ChangeDetectorRef));
  private source: Observable<T> | null = null;
  private wrapped: Observable<ApiState<T>> | null = null;

  transform(source: Observable<T> | null): ApiState<T> {
    if (source !== this.source) {
      this.source = source;
      this.wrapped = source
        ? source.pipe(
            map(data => ({ state: 'success' as const, data })),
            catchError(err =>
              of({
                state: 'error' as const,
                error: extractHttpErrorMessage(err),
              })
            ),
            startWith({ state: 'loading' as const })
          )
        : null;
    }

    return this.asyncPipe.transform(this.wrapped) ?? { state: 'loading' };
  }

  ngOnDestroy(): void {
    this.asyncPipe.ngOnDestroy();
  }
}
